import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  Table2, Loader2, Sparkles, ChevronDown, Copy, Download,
  RefreshCw, Check, FileDown, Trash2, Lock
} from 'lucide-react'
import client from '../api/client'
import toast from 'react-hot-toast'
import { useTokenStore } from '../store/tokenStore'

// Простая санитизация HTML без npm-пакетов
function sanitizeHtml(html: string): string {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/on\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\bon\w+\s*=\s*\S+/gi, '')
    .replace(/javascript\s*:/gi, '')
    .replace(/data\s*:\s*text\/html/gi, '')
}

/**
 * Парсит текстовую таблицу (markdown | или разделители ---) в HTML.
 * Поддерживает: обычные | таблицы, таблицы с --- разделителями,
 * несколько таблиц подряд, примечания после таблицы.
 */
/**
 * Чистит HTML-таблицу от мусорных строк (---, ***, ~~~ внутри ячеек).
 */
function cleanTableHtml(html: string): string {
  // Удаляем строки-разделители внутри <td> и <th>
  let cleaned = html
    .replace(/<td>[\s]*[-―—]{3,}[\s]*<\/td>/gi, '')
    .replace(/<th>[\s]*[-―—]{3,}[\s]*<\/th>/gi, '')
    .replace(/<tr>\s*<\/tr>/g, '') // пустые строки
    .replace(/<tr>\s*<\/tr>/g, '')
  return cleaned
}

/**
 * Преобразует сырой вывод AI в чистый HTML для отображения.
 * Таблицы (<table>) оставляет как есть, текст вне таблиц:
 * - экранирует HTML
 * - **_текст_** → <strong>текст</strong>
 * - нумерованные списки (1. 2. 3.) и маркированные (-) → <li>
 * - убирает ###, ---, ```, ```
 */
function formatAiOutput(raw: string): string {
  // Разбиваем на табличные блоки и текстовые блоки
  const parts = raw.split(/(<table>.*?<\/table>)/gs)
  return parts.map(part => {
    if (part.startsWith('<table>')) {
      return part
    }

    // === Экранируем HTML, но сохраняем переносы ===
    let text = part
      .replace(/</g, '&lt;').replace(/>/g, '&gt;')
      // Убираем markdown-обёртки
      .replace(/^```[\w]*\s*/gm, '')
      .replace(/\s*```$/gm, '')
      .replace(/[#]{1,6}\s*/g, '')
      // Убираем строки состоящие только из ---
      .replace(/^[\s]*[-―—]{3,}[\s]*$/gm, '')
      // Экранируем литералы: `code` → <code>code</code>
      .replace(/`([^`]+)`/g, '<code style="background:rgba(255,255,255,0.05);padding:1px 5px;border-radius:3px;font-size:12px">$1</code>')
      // **жирный** → <strong>
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // _курсив_ → <em>
      .replace(/_(.+?)_/g, '<em>$1</em>')
      .trim()

    if (!text) return ''

    // === Разбираем на строки и форматируем списки ===
    const lines = text.split('\n')
    const formattedLines: string[] = []
    let inList = false

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) {
        if (inList) {
          formattedLines.push('</ul>')
          inList = false
        }
        formattedLines.push('<br/>')
        continue
      }

      // Нумерованный список: "1. текст" или "1) текст"
      const numberedMatch = line.match(/^(\d+)[.).]\s+(.*)/)
      // Маркированный список: "- текст" или "* текст"
      const bulletMatch = line.match(/^[-*]\s+(.*)/)

      if (numberedMatch || bulletMatch) {
        const content = numberedMatch ? numberedMatch[2] : bulletMatch![1]
        if (!inList) {
          formattedLines.push('<ul style="list-style:none;padding:0;margin:4px 0">')
          inList = true
        }
        const marker = numberedMatch ? `<span style="color:#818cf8;margin-right:6px">${numberedMatch[1]}.</span>` : '<span style="color:#818cf8;margin-right:6px">•</span>'
        formattedLines.push(`<li style="color:#9ca3af;line-height:1.6;font-size:13px;margin:2px 0">${marker}${content}</li>`)
      } else {
        if (inList) {
          formattedLines.push('</ul>')
          inList = false
        }
        formattedLines.push(`<p style="color:#9ca3af;line-height:1.6;margin:4px 0;font-size:13px">${line}</p>`)
      }
    }
    if (inList) formattedLines.push('</ul>')

    return formattedLines.join('\n')
  }).filter(Boolean).join('\n')
}

function textTableToHtml(text: string): string {
  const rawLines = text.split('\n')
  if (rawLines.length < 2) return text

  // === Фаза 1: ищем блоки-таблицы ===
  // Табличный блок — строки, где есть | по краям, или строки с ---
  // Разбиваем текст на блоки: таблицы и не-таблицы
  const blocks: { type: 'pipe' | 'dash' | 'text', lines: string[] }[] = []
  let currentType: string | null = null
  let currentLines: string[] = []

  const flushBlock = () => {
    if (currentLines.length > 0 && currentType) {
      blocks.push({ type: currentType as any, lines: [...currentLines] })
      currentLines = []
    }
    currentType = null
  }

  for (const line of rawLines) {
    const trimmed = line.trim()
    const isPipe = trimmed.startsWith('|') && trimmed.endsWith('|')
    const isDash = /^[\s-]+$/.test(trimmed) && trimmed.length > 3
    const isEmpty = trimmed === ''

    if (isEmpty) {
      flushBlock()
      continue
    }

    if (isPipe) {
      if (currentType !== 'pipe') flushBlock()
      currentType = 'pipe'
      currentLines.push(trimmed)
    } else if (isDash) {
      // Разделитель --- внутри таблицы — пропускаем (это не новый блок)
      if (currentType !== 'pipe') {
        flushBlock()
        currentType = 'dash'
        currentLines.push(trimmed)
      }
      // Если мы в pipe-блоке — просто игнорируем эту строку (pipe-таблицы не используют ---)
    } else {
      if (currentType === 'pipe') {
        // Эта строка может быть частью текста после таблицы
        flushBlock()
        currentType = 'text'
        currentLines.push(trimmed)
      } else if (currentType === 'text') {
        currentLines.push(trimmed)
      } else {
        currentType = 'text'
        currentLines.push(trimmed)
      }
    }
  }
  flushBlock()

  // === Фаза 2: преобразуем каждый табличный блок в HTML ===
  let result = ''
  for (const block of blocks) {
    if (block.type === 'pipe') {
      // Markdown-таблица с |
      const dataLines = block.lines.filter(l => !/^\|[\s:-]+\|$/.test(l))
      if (dataLines.length < 2) {
        result += block.lines.join('\n') + '\n'
        continue
      }

      result += '<table>\n<thead>\n'
      dataLines.forEach((line, idx) => {
        const cells = line.split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1)
        const tag = idx === 0 ? 'th' : 'td'
        result += '<tr>'
        cells.forEach(cell => {
          const clean = cell.trim().replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          result += `<${tag}>${clean}</${tag}>`
        })
        result += '</tr>\n'
        if (idx === 0) result += '</thead>\n<tbody>\n'
      })
      result += '</tbody>\n</table>\n'

    } else if (block.type === 'dash') {
      // Текстовая таблица с разделителем --- под заголовком
      const dashIdx = block.lines.findIndex(l => /^[\s-]+$/.test(l) && l.length > 3)
      if (dashIdx < 1 || dashIdx >= block.lines.length - 1) {
        result += block.lines.join('\n') + '\n'
        continue
      }

      const headerLine = block.lines[dashIdx - 1]
      const dataLines = block.lines.slice(dashIdx + 1).filter(l => l.trim().length > 0)
      const parts = headerLine.split(/\s{2,}/).filter(Boolean)
      if (parts.length < 2) {
        result += block.lines.join('\n') + '\n'
        continue
      }

      result += '<table>\n<thead>\n<tr>'
      parts.forEach(p => { result += `<th>${p.trim()}</th>` })
      result += '</tr>\n</thead>\n<tbody>\n'
      dataLines.forEach(line => {
        const cells = line.split(/\s{2,}/).filter(Boolean)
        if (cells.length > 0) {
          result += '<tr>'
          cells.forEach(c => { result += `<td>${c.trim()}</td>` })
          result += '</tr>\n'
        }
      })
      result += '</tbody>\n</table>\n'

    } else {
      // Обычный текст — возвращаем как есть
      result += block.lines.join('\n') + '\n'
    }
  }

  return result.trim()
}

interface AiModelItem {
  id: number
  model_name: string
  display_name: string | null
  provider: string | null
  category: string
  is_paid: boolean
  icon_url: string | null
  is_active: boolean
  cost_per_million_usd: number | null
  sort_order: number | null
}

const EXAMPLES = [
  'Расписание уроков на неделю',
  'Бюджет на месяц с категориями доходов и расходов',
  'Список задач с приоритетами, статусом и дедлайнами',
  'Сравнение характеристик смартфонов 2024',
  'План тренировок на неделю',
  'Календарь отпусков сотрудников',
]

const CHAT_MODEL_PRICES: Record<string, {input: number; output: number}> = {
  'openai/gpt-5.4-nano': {input: 0, output: 2},
  'openai/gpt-5.4-mini': {input: 2, output: 9},
  'deepseek/deepseek-chat': {input: 0, output: 2},
  'google/gemini-2.5-flash': {input: 1, output: 5},
  'google/gemini-2.0-flash-001': {input: 1, output: 3},
}

export default function AITables() {
  const { balance } = useTokenStore()
  const isPaidLocked = (balance ?? 0) <= 0
  const [prompt, setPrompt] = useState('')
  const [generating, setGenerating] = useState(false)
  const [tableHtml, setTableHtml] = useState('')
  const [tableCsv, setTableCsv] = useState('')
  const [headers, setHeaders] = useState<string[]>([])
  const [tokensSpent, setTokensSpent] = useState(0)
  const [modelUsed, setModelUsed] = useState('')
  const [selectedModel, setSelectedModel] = useState('')
  const [serverModels, setServerModels] = useState<AiModelItem[]>([])
  const [showModelPicker, setShowModelPicker] = useState(false)
  const [copied, setCopied] = useState(false)
  const [editing, setEditing] = useState(false)
  const tableRef = useRef<HTMLDivElement>(null)

  const formatPrice = useCallback((m: AiModelItem) => {
    if (!m.is_paid) return null
    let inputPrice = 0
    let outputPrice = 0
    if (m.cost_per_million_usd && m.cost_per_million_usd > 0) {
      inputPrice = Math.round(m.cost_per_million_usd / 6 / 1000000 * 2 * 1000000)
      outputPrice = Math.max(1, Math.round(m.cost_per_million_usd * 5 / 6 / 1000000 * 2 * 1000000))
    } else {
      const hardcoded = CHAT_MODEL_PRICES[m.model_name]
      if (hardcoded) {
        inputPrice = hardcoded.input
        outputPrice = hardcoded.output
      }
    }
    if (outputPrice > 0) return `${inputPrice}/${outputPrice}`
    return null
  }, [])

  const sortedModels = useMemo(() => {
    const free = serverModels.filter(m => !m.is_paid).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
    const paid = serverModels.filter(m => m.is_paid).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
    return [...free, ...paid]
  }, [serverModels])

  // Load models
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await client.get('/models/ai', { params: { category: 'chat' } })
        const chatModels: AiModelItem[] = (data || []).filter((m: AiModelItem) => m.is_active && m.category === 'chat')
        setServerModels(chatModels)
        const free = chatModels.find(m => !m.is_paid)
        if (free) setSelectedModel(free.model_name)
        else if (chatModels.length > 0) setSelectedModel(chatModels[0].model_name)
      } catch {}
    }
    load()
  }, [])

  // Generate table
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Опишите таблицу, которую хотите создать')
      return
    }
    setGenerating(true)
    setTableHtml('')
    setTableCsv('')
    setHeaders([])
    setTokensSpent(0)
    setModelUsed('')
    try {
      const { data } = await client.post('/tables/generate', {
        prompt: prompt.trim(),
        model: selectedModel,
      })
      // Пробуем распарсить как HTML-таблицу. Если не получилось —
      // пробуем разные форматы текстовых таблиц (AI может игнорировать инструкцию)
      let html = (data.html || '').trim()
      
      // Если AI вернул HTML, но с мусором (--- в ячейках, лишние tr) — чистим
      if (html.startsWith('<')) {
        html = cleanTableHtml(html)
      } else {
        // Пробуем распарсить текстовую таблицу (markdown | или --- разделители)
        const parsed = textTableToHtml(html)
        if (parsed !== html) {
          html = parsed
        }
        // После парсинга тоже чистим мусор
        html = cleanTableHtml(html)
      }
      
      // Форматируем вывод: убираем markdown-символы из текста, чистим ### ** ---
      html = formatAiOutput(html)
      setTableHtml(sanitizeHtml(html))
      setTableCsv(data.csv || '')
      setHeaders(data.headers || [])
      setTokensSpent(data.tokens_spent || 0)
      setModelUsed(data.model_used || '')
      if (data.html) {
        toast.success('Таблица сгенерирована!')
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Ошибка генерации таблицы'
      toast.error(msg)
    } finally {
      setGenerating(false)
    }
  }

  // Copy CSV
  const handleCopyCsv = async () => {
    if (!tableCsv) return
    try {
      await navigator.clipboard.writeText(tableCsv)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success('CSV скопирован!')
    } catch {
      toast.error('Не удалось скопировать')
    }
  }

  // Download CSV
  const handleDownloadCsv = () => {
    if (!tableCsv) return
    const blob = new Blob([tableCsv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'table.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    toast.success('Скачано как CSV!')
  }

  // Current model
  const currentModel = serverModels.find(m => m.model_name === selectedModel)
  const currentModelPrice = currentModel ? formatPrice(currentModel) : null

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleGenerate()
    }
  }

  return (
    <div className="flex flex-col h-full bg-[#0f0f1a]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f0f1a]/95 backdrop-blur-sm border-b border-white/5 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-500/10">
              <Table2 className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">AI-Таблицы</h1>
              <p className="text-xs text-gray-500">Создавайте таблицы по описанию на естественном языке</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Model picker */}
            <div className="relative">
              <button
                onClick={() => setShowModelPicker(!showModelPicker)}
                className="input-field text-xs flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <Sparkles className="w-3 h-3 text-indigo-400" />
                <span className="max-w-[80px] truncate">{currentModel?.display_name || currentModel?.model_name || 'Модель'}</span>
                <span className="text-[9px] text-indigo-400/60">{currentModel?.is_paid ? '👑' : 'FREE'}</span>
                {currentModelPrice && <span className="text-[9px] text-gray-500">{currentModelPrice}</span>}
                <ChevronDown className="w-3 h-3 text-gray-500 shrink-0" />
              </button>
              {showModelPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowModelPicker(false)} />
                  <div className="absolute top-full right-0 mt-1 w-64 bg-[#1a1a2e] border border-white/10 rounded-xl z-20 p-1.5 max-h-64 overflow-y-auto shadow-2xl">
                    {sortedModels.filter(m => !m.is_paid).length > 0 && (
                      <>
                        <div className="px-2.5 py-1 text-[10px] font-semibold text-emerald-400/70 uppercase tracking-wider">Бесплатные</div>
                        {sortedModels.filter(m => !m.is_paid).map(m => (
                          <button key={m.model_name}
                            onClick={() => { setSelectedModel(m.model_name); setShowModelPicker(false) }}
                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 ${selectedModel === m.model_name ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'}`}>
                            <Sparkles className="w-3 h-3 shrink-0 text-emerald-400/60" />
                            <span className="truncate flex-1">{m.display_name || m.model_name}</span>
                            <span className="text-[9px] text-emerald-400/50 shrink-0">FREE</span>
                          </button>
                        ))}
                      </>
                    )}
                    {sortedModels.filter(m => m.is_paid).length > 0 && (
                      <>
                        <div className="px-2.5 py-1 text-[10px] font-semibold text-amber-400/70 uppercase tracking-wider mt-1">Платные</div>
                        {sortedModels.filter(m => m.is_paid).map(m => {
                          const price = formatPrice(m)
                          return (
                            <button key={m.model_name}
                              onClick={() => {
                                if (isPaidLocked) { toast.error('Недостаточно токенов'); return }
                                setSelectedModel(m.model_name); setShowModelPicker(false)
                              }}
                              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 ${selectedModel === m.model_name ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'} ${isPaidLocked ? 'opacity-40 cursor-not-allowed' : ''}`}>
                              {isPaidLocked && <Lock className="w-3 h-3 shrink-0 text-amber-400/60" />}
                              {!isPaidLocked && <Sparkles className="w-3 h-3 shrink-0 text-indigo-400/60" />}
                              <span className="truncate flex-1">{m.display_name || m.model_name}</span>
                              {price && <span className="text-[9px] text-gray-500 shrink-0">{price}</span>}
                              <span className="text-[9px] text-amber-400/50 shrink-0">👑</span>
                            </button>
                          )
                        })}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Input section */}
          <div className="rounded-xl bg-white/[0.02] border border-white/5 p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Опишите таблицу, которую хотите создать
              </label>
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Например: расписание уроков на неделю с указанием времени, предмета и учителя"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-sm text-gray-200 placeholder-gray-600 outline-none focus:border-indigo-500/40 transition-colors resize-none"
                rows={3}
              />
            </div>

            {/* Examples */}
            <div>
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-2">Примеры запросов</p>
              <div className="flex flex-wrap gap-1.5">
                {EXAMPLES.map((example, i) => (
                  <button
                    key={i}
                    onClick={() => setPrompt(example)}
                    className="px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/5 text-[11px] text-gray-400 hover:text-gray-200 hover:border-indigo-500/30 hover:bg-indigo-500/5 transition-all"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={generating || !prompt.trim()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed border border-indigo-500/20"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Генерирую таблицу…</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Сгенерировать таблицу</>
              )}
            </button>

            {prompt.trim() && !generating && (
              <p className="text-[10px] text-gray-600">
                <kbd className="px-1 py-0.5 rounded bg-white/5 text-[10px] font-mono">Ctrl+Enter</kbd> для быстрой отправки
              </p>
            )}
          </div>

          {/* Result section */}
          {(tableHtml || generating) && (
            <div className="rounded-xl bg-white/[0.02] border border-white/5 overflow-hidden">
              {/* Toolbar */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <Table2 className="w-4 h-4 text-indigo-400" />
                  <span className="text-sm font-medium text-white">Таблица</span>
                  {headers.length > 0 && (
                    <span className="text-[10px] text-gray-500">
                      {headers.length} колонок
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {tableHtml && (
                    <>
                      <button
                        onClick={() => {
                          const blob = new Blob([tableHtml], { type: 'text/html;charset=utf-8;' })
                          const url = URL.createObjectURL(blob)
                          const link = document.createElement('a')
                          link.href = url
                          link.download = 'table.html'
                          document.body.appendChild(link)
                          link.click()
                          document.body.removeChild(link)
                          URL.revokeObjectURL(url)
                          toast.success('Скачано как HTML')
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all text-xs"
                        title="Скачать HTML"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Скачать HTML</span>
                      </button>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(tableHtml)
                          toast.success('HTML скопирован')
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all text-xs"
                        title="Копировать HTML"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {tableCsv && (
                    <>
                      <button
                        onClick={handleCopyCsv}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all text-xs"
                        title="Копировать CSV"
                      >
                        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        <span className="hidden sm:inline">{copied ? 'Скопировано' : 'CSV'}</span>
                      </button>
                      <button
                        onClick={handleDownloadCsv}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all text-xs"
                        title="Скачать CSV"
                      >
                        <FileDown className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">CSV</span>
                      </button>
                    </>
                  )}
                  {tableHtml && (
                    <button
                      onClick={() => {
                        setTableHtml('')
                        setTableCsv('')
                        setHeaders([])
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-all text-xs"
                      title="Удалить таблицу"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Удалить</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="p-5 overflow-x-auto">
                {generating ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
                    <span className="text-sm text-gray-500">Генерация таблицы…</span>
                  </div>
                ) : (
                  <div
                    ref={tableRef}
                    className="min-w-full"
                    contentEditable
                    suppressContentEditableWarning
                    onFocus={() => setEditing(true)}
                    onBlur={() => {
                      setEditing(false)
                      // Update HTML from editable content
                      if (tableRef.current) {
                        setTableHtml(tableRef.current.innerHTML)
                      }
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(tableHtml) }}
                    style={{
                      outline: 'none',
                      cursor: editing ? 'text' : 'default',
                    }}
                  />
                )}
              </div>

              {!generating && tableHtml && (
                <div className="px-5 py-3 border-t border-white/5 flex flex-col sm:flex-row sm:items-center gap-1.5 text-[10px] text-gray-500">
                  <span>💡 Таблицу можно редактировать прямо в браузере — кликните по ячейке для изменения</span>
                  <span className="hidden sm:inline mx-1.5 text-gray-700">·</span>
                  <span>
                    Использовано: <span className="text-indigo-400/70 font-medium">{tokensSpent.toLocaleString()}</span> токенов
                    {modelUsed && <> · модель: <span className="text-indigo-400/70">{modelUsed}</span></>}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {!tableHtml && !generating && (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <div className="p-4 rounded-2xl bg-indigo-500/5 border border-indigo-500/10">
                <Table2 className="w-12 h-12 text-indigo-500/30" />
              </div>
              <div>
                <p className="text-gray-400 font-medium">Создайте свою первую таблицу</p>
                <p className="text-xs text-gray-600 mt-1">
                  Опишите на естественном языке, какую таблицу вам нужно, и AI создаст её
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {EXAMPLES.slice(0, 3).map((example, i) => (
                  <button
                    key={i}
                    onClick={() => setPrompt(example)}
                    className="px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/5 text-xs text-gray-400 hover:text-gray-200 hover:border-indigo-500/30 transition-all"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Inline styles for table */}
      <style>{`
        .contentEditable-table table,
        [contenteditable] table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
          color: #e5e7eb;
        }
        [contenteditable] thead th {
          background: rgba(99, 102, 241, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 10px 14px;
          text-align: left;
          font-weight: 600;
          color: #a5b4fc;
          white-space: nowrap;
        }
        [contenteditable] tbody td {
          border: 1px solid rgba(255, 255, 255, 0.06);
          padding: 8px 14px;
          color: #d1d5db;
          transition: background 0.15s;
        }
        [contenteditable] tbody tr:hover td {
          background: rgba(255, 255, 255, 0.03);
        }
        [contenteditable] tbody td:hover {
          background: rgba(99, 102, 241, 0.05);
        }
        [contenteditable]:focus table {
          border-color: rgba(99, 102, 241, 0.3);
        }
      `}</style>
    </div>
  )
}
